// Registers the docs asset cache service worker (public/docs-asset-sw.js).
// The worker transparently caches authenticated docs images with a 24h TTL so
// they are only loaded from the server once until they go stale. Registration
// is best-effort: if it fails or the browser lacks support, docs images still
// load normally — just without client-side caching.

import { datadogLogs } from '@datadog/browser-logs';

// The marker tells the worker that keys its cache by session from the path-keyed one it replaced.
const SERVICE_WORKER_URL = '/docs-asset-sw.js?keyed=session';
const WORKER_TAKEOVER_TIMEOUT_MS = 10_000;

export function registerDocsAssetServiceWorker(): void {
  // jsdom (and any non-browser host) lacks serviceWorker, so this is a safe
  // no-op under tests without needing an explicit environment guard.
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener(
    'load',
    () => {
      navigator.serviceWorker.register(SERVICE_WORKER_URL).catch((error) => {
        // Non-fatal — caching is an enhancement, not a requirement.
        datadogLogs.logger.warn('Docs asset service worker registration failed', { error });
      });
    },
    { once: true }
  );
}

// The path-keyed worker this one replaced could serve one session's images to another, so a docs image is
// requested only when no worker controls the page or the session-keyed one does. While the old one still
// does, images wait for the takeover, and are withheld if it does not come.
export function docsAssetWorkerIsSessionKeyed(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return Promise.resolve(true);

  const workers = navigator.serviceWorker;
  const sessionKeyed = () => !workers.controller || workers.controller.scriptURL.endsWith(SERVICE_WORKER_URL);
  if (sessionKeyed()) return Promise.resolve(true);

  return new Promise((resolve) => {
    const settle = () => {
      if (!sessionKeyed()) return;
      clearTimeout(timeout);
      workers.removeEventListener('controllerchange', settle);
      resolve(true);
    };
    const timeout = setTimeout(() => {
      workers.removeEventListener('controllerchange', settle);
      resolve(sessionKeyed());
    }, WORKER_TAKEOVER_TIMEOUT_MS);
    workers.addEventListener('controllerchange', settle);
  });
}
